(function() {
  class MTRelatedProduct {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector('.mt-related-product__grid');
      // Select all buttons (both desktop and mobile) to ensure mobile buttons work
      this.prevBtns = section.querySelectorAll('.mt-related-product__arrow--prev');
      this.nextBtns = section.querySelectorAll('.mt-related-product__arrow--next');
      // Keep single references for backward compatibility
      this.prevBtn = this.prevBtns[0] || null;
      this.nextBtn = this.nextBtns[0] || null;
      this.emptyState = section.querySelector('.mt-related-product__empty');
      this.limit = parseInt(section.dataset.limit || '4', 10);
      this.productId = section.dataset.productId;
      this.endpoint = section.dataset.endpoint;
      this.intent = section.dataset.intent || 'related';
      this.fallbackEndpoint = section.dataset.fallbackEndpoint;
      this.useOnlyCollection = section.dataset.useOnlyCollection === 'true'; // 是否仅使用指定collection
      // 若未传 rootUrl，则退回当前站点 origin
      const root = section.dataset.rootUrl || window.location.origin || '';
      this.rootUrl = root.replace(/\/$/, '');
      this.items = [];
      this.pointer = 0;
      this.moneyFormat = section.dataset.moneyFormat || window?.Shopify?.currency?.active || '';
      this.loaded = false;
      this.intersectionObserver = null;
      this.isMobile = window.innerWidth <= 749;
      this.displayedCount = 0; // 已显示的产品数量
      this.isLoadingMore = false; // 是否正在加载更多
      this.scrollObserver = null; // 滚动观察器
      this.loadedProductIds = new Set(); // 已加载的产品ID集合，避免重复
      this.currentPage = 1; // 当前页码（用于 collection 分页）
      this.hasMoreProducts = true; // 是否还有更多产品
      this.isUsingFallback = false; // 是否使用 fallback endpoint
      /** @type {HTMLElement | null} */
      this.loadingIndicator = section.querySelector('.mt-related-product__loading');

      // 检查必要条件：必须有 grid，并且（有 productId 和 endpoint）或者（有 fallbackEndpoint 或 useOnlyCollection）
      if (!this.grid) return;
      const hasProductIdAndEndpoint = this.productId && this.endpoint;
      const hasFallbackOrUseOnlyCollection = this.fallbackEndpoint || this.useOnlyCollection;
      
      if (!hasProductIdAndEndpoint && !hasFallbackOrUseOnlyCollection) {
        console.warn('[mt-related-product] Missing required data: need either (productId + endpoint) or (fallbackEndpoint or useOnlyCollection)');
        return;
      }

      this.bindEvents();
      this.observe();
      this.handleResize();
      window.addEventListener('resize', () => this.handleResize());
    }

    bindEvents() {
      // 移动端不绑定切换按钮事件，使用无限滚动
      if (!this.isMobile) {
        // Bind all prev buttons (desktop only)
        this.prevBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.prev());
        });
        // Bind all next buttons (desktop only)
        this.nextBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.next());
        });
      }
      this.updateArrows();
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 749;
      if (wasMobile !== this.isMobile && this.loaded) {
        // 重新绑定事件
        this.bindEvents();
        // 如果切换到移动端，重置显示数量为4
        if (this.isMobile && this.items.length > 0) {
          this.displayedCount = Math.min(4, this.items.length);
        } else if (!this.isMobile) {
          // 切换到桌面端，使用分页逻辑（每次显示4个）
          this.pointer = 0;
        }
        this.render();
        // 只在移动端重新设置无限滚动
        if (this.isMobile) {
          setTimeout(() => {
            this.setupInfiniteScroll();
          }, 100);
        }
      }
    }

    observe() {
      // 延迟加载，参考 product-recommendations 行为
      this.intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.load();
          // 注意：load() 是异步的，无限滚动会在 load() 内部设置
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.intersectionObserver.observe(this.section);
    }

    async load() {
      if (this.loaded && !this.isMobile) return; // 桌面端只加载一次
      if (this.isLoadingMore) return; // 防止重复加载
      if (!this.hasMoreProducts && this.loaded && this.isMobile) return; // 移动端：没有更多产品了
      
      this.isLoadingMore = true;
      this.showLoading();
      
      // 首次加载
      if (!this.loaded) {
        this.loaded = true;
        try {
          // 如果设置了"仅使用指定collection"或指定了collection，优先使用collection
          if (this.useOnlyCollection || this.fallbackEndpoint) {
            if (!this.fallbackEndpoint) {
              console.warn('[mt-related-product] useOnlyCollection is true but no fallbackEndpoint');
              this.showEmpty();
              this.hasMoreProducts = false;
              this.isLoadingMore = false;
              this.hideLoading();
              return;
            }
            
            this.isUsingFallback = true;
            this.currentPage = 1;
            const initialPageSize = this.isMobile ? 4 : 50;
            // 确保使用相对路径或修复错误的 URL
            let initialFallbackUrl = this.fallbackEndpoint.replace(/limit=\d+/, `limit=${initialPageSize}`);
            // 如果 URL 不是以 / 开头，说明可能是完整 URL，需要修复
            if (!initialFallbackUrl.startsWith('/') && !initialFallbackUrl.startsWith('http')) {
              // 如果包含错误的域名拼接，提取路径部分
              const pathMatch = initialFallbackUrl.match(/\/collections\/[^\/]+\/products\.json(\?.*)?$/);
              if (pathMatch) {
                initialFallbackUrl = pathMatch[0].replace(/limit=\d+/, `limit=${initialPageSize}`);
              }
            }
            // 如果是相对路径，使用当前页面的 origin
            if (initialFallbackUrl.startsWith('/')) {
              initialFallbackUrl = window.location.origin + initialFallbackUrl;
            }
            
            console.log('[mt-related-product] Fetching collection:', initialFallbackUrl);
            const fallbackItems = await this.fetchProducts(initialFallbackUrl, true);
            console.log('[mt-related-product] Collection result:', {
              fetched: fallbackItems.length,
              currentProductId: this.productId
            });
            
            if (fallbackItems.length > 0) {
              this.addItems(fallbackItems);
            } else {
              console.warn('[mt-related-product] Collection returned 0 products. Possible reasons:');
              console.warn('1. Collection is empty');
              console.warn('2. Collection only contains current product (filtered out)');
              console.warn('3. API request failed');
              
              // 如果"仅使用指定集合"但集合为空，不再尝试推荐API
              if (this.useOnlyCollection) {
                // 已经尝试了集合，但没有产品，直接显示空状态
              } else {
                // 如果没有启用"仅使用指定集合"，尝试使用推荐API作为备选
                if (this.endpoint) {
                  const recItems = await this.fetchProducts(this.endpoint);
                  if (recItems.length > 0) {
                    this.addItems(recItems);
                  }
                }
              }
            }
          } else {
            let recItems = [];
            // 1. 先获取推荐 API 的产品（Shopify 推荐 API 最多返回 10 个产品）
            if (this.endpoint) {
              recItems = await this.fetchProducts(this.endpoint);
              if (recItems.length > 0) {
                this.addItems(recItems);
              }
            }

            // 2. 如果推荐产品不够（少于10个）且有 fallback collection，从 fallback collection 补充
            const minRecommendedCount = 10; // 期望的最少推荐产品数量
            if (this.items.length < minRecommendedCount && this.fallbackEndpoint) {
              this.isUsingFallback = true;
              this.currentPage = 1;
              // 计算需要补充的数量
              const neededCount = minRecommendedCount - this.items.length;
              // 移动端首次只加载4个产品，桌面端加载更多
              const initialPageSize = this.isMobile ? 4 : Math.max(neededCount, 50);
              let initialFallbackUrl = this.fallbackEndpoint.replace(/limit=\d+/, `limit=${initialPageSize}`);
              // 如果是相对路径，使用当前页面的 origin
              if (initialFallbackUrl.startsWith('/')) {
                initialFallbackUrl = window.location.origin + initialFallbackUrl;
              }
              const fallbackItems = await this.fetchProducts(initialFallbackUrl, true);
              if (fallbackItems.length > 0) {
                this.addItems(fallbackItems);
              }
            } else if (this.fallbackEndpoint && (!this.endpoint || recItems.length === 0)) {
              // 如果推荐 API 没有返回产品（或没有 endpoint），使用 fallback collection
              this.isUsingFallback = true;
              this.currentPage = 1;
              const initialPageSize = this.isMobile ? 4 : 50;
              let initialFallbackUrl = this.fallbackEndpoint.replace(/limit=\d+/, `limit=${initialPageSize}`);
              // 如果是相对路径，使用当前页面的 origin
              if (initialFallbackUrl.startsWith('/')) {
                initialFallbackUrl = window.location.origin + initialFallbackUrl;
              }
              const fallbackItems = await this.fetchProducts(initialFallbackUrl, true);
              if (fallbackItems.length > 0) {
                this.addItems(fallbackItems);
              }
            }
          }

          // 3. 设置显示数量
          if (this.items.length > 0) {
            // 移动端初始显示4个，桌面端使用分页逻辑（每次显示4个）
            if (this.isMobile) {
              this.displayedCount = Math.min(4, this.items.length);
            } else {
              // 桌面端：重置 pointer 为 0，使用分页逻辑
              this.pointer = 0;
            }
            this.render();
            // 只在移动端设置无限滚动
            if (this.isMobile) {
              setTimeout(() => {
                this.setupInfiniteScroll();
              }, 100);
            }
            this.isLoadingMore = false;
            this.hideLoading();
            return;
          }

          this.showEmpty();
          this.hasMoreProducts = false;
        } catch (e) {
          this.showEmpty();
          this.hasMoreProducts = false;
        }
      } else if (this.isMobile) {
        // 加载更多产品
        if (this.isMobile) {
          // 移动端：如果还有未显示的产品，先显示它们
          if (this.displayedCount < this.items.length) {
            this.displayedCount = Math.min(this.displayedCount + 4, this.items.length);
            this.render();
            this.hideLoading();
            
            // 重新设置无限滚动（桌面端和移动端都支持）
            setTimeout(() => {
              this.setupInfiniteScroll();
            }, 100);
            
            this.isLoadingMore = false;
            return;
          }
        }
        
        // 需要从 API 获取更多产品（移动端）
        if (this.isUsingFallback && this.fallbackEndpoint) {
          // 使用 fallback collection，可以分页加载
          this.currentPage++;
          // 移动端每次只加载4个产品
          const pageSize = this.isMobile ? 4 : 50;
          let fallbackUrl = this.fallbackEndpoint.replace(/limit=\d+/, `limit=${pageSize}`);
          // 如果是相对路径，使用当前页面的 origin
          if (fallbackUrl.startsWith('/')) {
            fallbackUrl = window.location.origin + fallbackUrl;
          }
          const pageUrl = fallbackUrl.includes('?') 
            ? `${fallbackUrl}&page=${this.currentPage}`
            : `${fallbackUrl}?page=${this.currentPage}`;
          
          try {
            const newItems = await this.fetchProducts(pageUrl, true);
            if (newItems.length > 0) {
              this.addItems(newItems);
              // 移动端每次只显示4个新产品
              if (this.isMobile) {
                this.displayedCount = Math.min(this.displayedCount + 4, this.items.length);
              } else {
                this.displayedCount = this.items.length;
              }
              this.render();
              this.hideLoading();
              
              // 只在移动端重新设置无限滚动
              if (this.isMobile) {
                setTimeout(() => {
                  this.setupInfiniteScroll();
                }, 100);
              }
            } else {
              // 没有更多产品了
              this.hasMoreProducts = false;
              this.hideLoading();
            }
          } catch (e) {
            this.hasMoreProducts = false;
            this.hideLoading();
          }
        } else {
          // 如果只使用了推荐 API（没有 fallback），无法加载更多（推荐 API 不支持分页）
          // 如果有 fallback collection，应该已经设置了 isUsingFallback
          if (!this.isUsingFallback && this.fallbackEndpoint) {
            // 如果之前只用了推荐 API，现在切换到 fallback collection
            this.isUsingFallback = true;
            this.currentPage = 1;
            const pageSize = this.isMobile ? 4 : 50;
            let fallbackUrl = this.fallbackEndpoint.replace(/limit=\d+/, `limit=${pageSize}`);
            // 如果是相对路径，使用当前页面的 origin
            if (fallbackUrl.startsWith('/')) {
              fallbackUrl = window.location.origin + fallbackUrl;
            }
            const pageUrl = fallbackUrl.includes('?') 
              ? `${fallbackUrl}&page=${this.currentPage}`
              : `${fallbackUrl}?page=${this.currentPage}`;
            
            try {
              const newItems = await this.fetchProducts(pageUrl, true);
              if (newItems.length > 0) {
                this.addItems(newItems);
                if (this.isMobile) {
                  this.displayedCount = Math.min(this.displayedCount + 4, this.items.length);
                } else {
                  this.displayedCount = this.items.length;
                }
                this.render();
                this.hideLoading();
                
                if (this.isMobile) {
                  setTimeout(() => {
                    this.setupInfiniteScroll();
                  }, 100);
                }
              } else {
                this.hasMoreProducts = false;
                this.hideLoading();
              }
            } catch (e) {
              this.hasMoreProducts = false;
              this.hideLoading();
            }
          } else {
            // 推荐 API 通常不支持分页，标记为没有更多产品
            this.hasMoreProducts = false;
            this.hideLoading();
          }
        }
      }
      
      this.isLoadingMore = false;
    }
    
    // 添加产品到列表，避免重复
    addItems(newItems) {
      newItems.forEach(item => {
        if (!this.loadedProductIds.has(String(item.id))) {
          this.items.push(item);
          this.loadedProductIds.add(String(item.id));
        }
      });
    }

    async fetchProducts(url, isFallback = false) {
      try {
        // 修复 URL：如果 URL 包含错误的域名拼接，使用当前页面的 origin
        let fetchUrl = url;
        if (isFallback && url) {
          // 检查 URL 是否包含错误的域名拼接（如 artzuzu.comgwfzhu-vx.myshopify.com）
          const urlObj = new URL(url, window.location.origin);
          // 如果 URL 的 host 看起来有问题，使用当前页面的 origin
          if (urlObj.host.includes('.myshopify.com') && !urlObj.host.match(/^[a-z0-9-]+\.myshopify\.com$/i)) {
            // URL 格式错误，使用当前页面的 origin 重新构建
            const pathMatch = url.match(/\/collections\/[^\/]+\/products\.json(\?.*)?$/);
            if (pathMatch) {
              fetchUrl = window.location.origin + pathMatch[0];
            }
          }
        }
        
        const res = await fetch(fetchUrl);
        if (!res.ok) {
          throw new Error(`Fetch failed: ${res.status}`);
        }
        const data = await res.json();
        const products = Array.isArray(data?.products) ? data.products : [];
        
        const normalized = products
          .map((p) => this.normalizeProduct(p))
          .filter((p) => {
            if (!p) return false;
            // 如果没有 productId，不过滤任何产品
            if (!this.productId) return true;
            // 过滤掉当前产品
            return String(p.id) !== String(this.productId);
          });
        
        // 临时调试信息
        if (normalized.length === 0 && products.length > 0) {
          console.warn('[mt-related-product] All products filtered out:', {
            url: fetchUrl,
            rawCount: products.length,
            currentProductId: this.productId,
            productIds: products.map(p => p.id)
          });
        }
        
        return normalized;
      } catch (e) {
        console.error('[mt-related-product] Fetch error:', e, { url, isFallback });
        return [];
      }
    }

    normalizeProduct(p) {
      if (!p || !p.title) return null;

      // URL 兼容：Shopify 推荐接口有 url；collections/{handle}/products.json 只有 handle
      const handle = p.handle || p.product_handle;
      const base = this.rootUrl || window.Shopify?.routes?.root || '';
      const url = p.url || (handle ? `${base}/products/${handle}` : null);
      if (!url) return null;

      // 图片兼容
      const imageObj = p.featured_image || p.images?.[0] || p.image || null;
      const imageSrc = imageObj?.src || imageObj;
      const alt = imageObj?.alt || p.title;

      // Title formatting: for titles like
      // "Artzuzu Custom Portrait For Pets - The Chef"
      // we only show the part after the last " - " (e.g. "The Chef")
      let displayTitle = p.title;
      if (typeof p.title === 'string') {
        const parts = p.title.split(' - ');
        if (parts.length > 1) {
          displayTitle = parts[parts.length - 1].trim();
        }
      }

      // 价格兼容：推荐接口用 price；products.json 需要从 variants 取
      const price =
        p.price ??
        p.price_min ??
        p?.variants?.[0]?.price ??
        null;
      const compareAt =
        p.compare_at_price ??
        p.compare_at_price_min ??
        p?.variants?.[0]?.compare_at_price ??
        null;

      return {
        id: p.id,
        title: displayTitle,
        url,
        image: imageSrc || null,
        alt,
        price: this.toCents(price),
        compare_at_price: this.toCents(compareAt),
      };
    }

    toCents(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return value > 1000 ? Math.round(value) : Math.round(value * 100);
      }
      const num = parseFloat(value);
      if (Number.isNaN(num)) return null;
      return num > 1000 ? Math.round(num) : Math.round(num * 100);
    }

    next() {
      // 桌面端：每次显示4个产品
      const itemsPerPage = 4;
      if (this.items.length <= itemsPerPage) return;
      const totalPages = Math.ceil(this.items.length / itemsPerPage);
      const maxPointer = (totalPages - 1) * itemsPerPage;
      this.pointer = Math.min(this.pointer + itemsPerPage, maxPointer);
      this.render();
    }

    prev() {
      // 桌面端：每次显示4个产品
      const itemsPerPage = 4;
      if (this.items.length <= itemsPerPage) return;
      this.pointer = Math.max(this.pointer - itemsPerPage, 0);
      this.render();
    }

    render() {
      if (!this.grid) return;
      
      // 记录之前的显示数量，用于判断哪些是新加载的产品（在清空之前）
      const previousCount = this.grid.children.length;
      this.grid.innerHTML = '';
      
      let slice = [];
      if (this.isMobile) {
        // 移动端：显示所有已加载的产品（最多 displayedCount 个）
        // 安全检查：确保 displayedCount 不会意外超过 items.length
        const maxDisplay = Math.min(this.displayedCount, this.items.length);
        // 如果 displayedCount 为 0 或未初始化，默认显示 4 个
        const finalCount = maxDisplay > 0 ? maxDisplay : Math.min(4, this.items.length);
        slice = this.items.slice(0, finalCount);
      } else {
        // 桌面端：始终使用分页逻辑，每次显示4个产品
        const itemsPerPage = 4;
        for (let i = 0; i < itemsPerPage && (this.pointer + i) < this.items.length; i += 1) {
          const idx = this.pointer + i;
          slice.push(this.items[idx]);
        }
      }

      slice.forEach((item, index) => {
        const priceHtml = this.buildPrice(item.price, item.compare_at_price);
        const imageHtml = item.image
          ? `<img class="mt-product-collection__image" src="${item.image}" alt="${this.escape(
              item.alt
            )}" loading="lazy">`
          : `<div class="mt-product-collection__image" style="background:#f5f5f5;"></div>`;

        const card = document.createElement('div');
        card.setAttribute('data-product-id', item.id);
        // 如果是新加载的产品（索引大于之前的数量），添加 is-new 类用于动画
        const isNewItem = this.isMobile && index >= previousCount;
        card.className = `mt-product-collection__item${isNewItem ? ' is-new' : ''}  product-card`;
        card.innerHTML = `
          <a href="${item.url}" class="mt-product-collection__product-link">
            <div class="mt-product-collection__image-wrapper">
              ${imageHtml}
            </div>
            <div class="mt-product-collection__info">
              <h3 class="mt-product-collection__product-title">${this.escape(item.title)}</h3>
              ${priceHtml}
              <button type="button" class="mt-product-collection__button">Shop Now</button>
            </div>
          </a>
        `;
        const btn = card.querySelector('.mt-product-collection__button');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            window.location.href = item.url;
          });
        }
        this.grid.appendChild(card);
      });

      this.updateArrows();
    }

    setupInfiniteScroll() {
      // 只在移动端启用无限滚动
      if (!this.isMobile || !this.grid) {
        return;
      }
      
      // 移除旧的观察器
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }
      
      // 如果没有更多产品了，不需要无限滚动
      if (!this.hasMoreProducts && this.displayedCount >= this.items.length) {
        return;
      }
      
      // 获取最后一个产品卡片
      const items = this.grid.querySelectorAll('.mt-product-collection__item');
      if (items.length === 0) {
        return;
      }
      
      const lastItem = items[items.length - 1];
      
      // 使用 IntersectionObserver 监听最后一个产品卡片是否进入视口
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && !this.isLoadingMore) {
            // 最后一个产品进入视口，加载更多
            // 添加小延迟，避免快速滚动时频繁触发
            setTimeout(() => {
              if (!this.isLoadingMore) {
                // 移动端：检查是否还有未显示的产品
                if (this.isMobile && this.displayedCount < this.items.length) {
                  this.load();
                } 
                // 桌面端或移动端：检查是否还有更多产品需要从 API 加载
                else if (this.hasMoreProducts) {
                  this.load();
                }
              }
            }, 200);
          }
        },
        {
          root: null, // 使用视口作为根
          rootMargin: '150px', // 提前150px触发加载
          threshold: 0.1
        }
      );
      
      this.scrollObserver.observe(lastItem);
    }

    buildPrice(price, compareAt) {
      const formattedPrice = this.formatMoney(price);
      const hasSale = compareAt && compareAt > price;
      if (hasSale) {
        return `
          <div class="mt-product-collection__prices">
            <span class="mt-product-collection__price mt-product-collection__price--compare">${this.formatMoney(
              compareAt
            )}</span>
            <span class="mt-product-collection__price mt-product-collection__price--sale">${formattedPrice}</span>
          </div>
        `;
      }
      return `
        <div class="mt-product-collection__prices">
          <span class="mt-product-collection__price">${formattedPrice}</span>
        </div>
      `;
    }

    formatMoney(cents) {
      const value = parseInt(cents, 10);
      if (Number.isNaN(value)) return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(value);
      }
      const number = (value / 100).toFixed(2);
      return this.moneyFormat ? `${this.moneyFormat} ${number}` : `$${number}`;
    }

    escape(str) {
      if (!str) return '';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    showEmpty() {
      if (this.emptyState) this.emptyState.style.display = 'block';
      if (this.grid) this.grid.innerHTML = '';
      this.hideLoading();
    }

    showLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.add('is-active');
      }
    }

    hideLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.remove('is-active');
      }
    }

    updateArrows() {
      // 移动端不显示箭头
      if (this.isMobile) {
        this.prevBtns.forEach((btn) => {
          btn.style.display = 'none';
        });
        this.nextBtns.forEach((btn) => {
          btn.style.display = 'none';
        });
        return;
      }
      
      // Update all buttons (desktop only)
      const total = this.items.length;
      const itemsPerPage = 4; // 桌面端每次显示4个产品
      let prevOpacity = '0.7';
      let nextOpacity = '0.7';
      let prevDisabled = false;
      let nextDisabled = false;

      if (total === 0 || total <= itemsPerPage) {
        // 没有产品或产品数量不超过4个，禁用切换按钮
        prevOpacity = '0.7';
        nextOpacity = '0.7';
        prevDisabled = true;
        nextDisabled = true;
      } else {
        // 产品数量超过4个，启用切换按钮
        const totalPages = Math.ceil(total / itemsPerPage);
        const currentPage = Math.floor(this.pointer / itemsPerPage);
        prevOpacity = currentPage === 0 ? '0.7' : '1';
        nextOpacity = currentPage >= totalPages - 1 ? '0.7' : '1';
        prevDisabled = currentPage === 0;
        nextDisabled = currentPage >= totalPages - 1;
      }

      // Update all prev buttons (desktop and mobile)
      this.prevBtns.forEach((btn) => {
        btn.style.opacity = prevOpacity;
        btn.disabled = prevDisabled;
      });

      // Update all next buttons (desktop and mobile)
      this.nextBtns.forEach((btn) => {
        btn.style.opacity = nextOpacity;
        btn.disabled = nextDisabled;
      });
    }
  }

  function init() {
    const sections = document.querySelectorAll('[data-mt-related-product]');
    sections.forEach((section) => new MTRelatedProduct(section));
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  }
})();

